import React, { useCallback, useMemo, useState, useEffect, useRef, memo } from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { IConfigStateGroups } from 'constants/interfaces'
import { FixedSizeList as List } from 'react-window'
import { TreeNode } from './TreeNode'
import { FlatNode, useFlatTree } from './hooks/useGroupsTree'
import useSelection, { SelectionMode } from './hooks/useSelection'
import { SearchInput } from 'components/ui/Input/SearchInput'
import { Button } from 'components/ui/Button'
import { NoResults } from '../GroupsTree/NoResults'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import colors from 'constants/colors'
import useLocaleText from 'components/useLocaleText'

const ROW_HEIGHT = 60

interface Props {
  groups: IConfigStateGroups
  allowedGroups: string[]
  selectionMode?: SelectionMode
  showSearch?: boolean
  searchWidth?: string
  searchActions?: React.ReactNode
  listHeight?: number
  initialSelectedIds?: string[]
  onSelectionChange?: (selectedIds: string[]) => void
  isShowTags?: boolean
  isCloseMode?: boolean
  selectSearchResult?: boolean
}

const OptimizedGroupsTree: React.FC<Props> = memo(
  ({
    groups,
    allowedGroups,
    showSearch = true,
    searchWidth = '100%',
    searchActions,
    selectionMode = SelectionMode.MULTIPLE,
    listHeight = 600,
    initialSelectedIds = [],
    onSelectionChange,
    isShowTags = false,
    isCloseMode = false,
    selectSearchResult = false,
  }) => {
    const isRtl = useSelector(isRtlSelector)
    const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null)
    const needsScrollRef = useRef<boolean>(false)
    const t_select_all = useLocaleText('select_all_stores_button')
    const t_clear = useLocaleText('group_unselecte_filter')
    const searchLabel = useLocaleText('search')

    const {
      visibleNodes,
      toggleNode,
      openNodes,
      searchTerm,
      searchResult,
      handleSearch,
      listRef,
      calculateIndeterminateState,
      getChildrenIds,
      expandToNode,
      clearSearch,
    } = useFlatTree(groups, allowedGroups, selectionMode, isCloseMode)

    const selectionOptions = useMemo(
      () => ({
        mode: selectionMode,
        initialSelectedIds,
        allIds: allowedGroups,
        getChildrenIds,
        openNodes,
        onSelectionChange,
      }),
      [selectionMode, initialSelectedIds, allowedGroups, getChildrenIds, openNodes, onSelectionChange]
    )

    const { toggleSelection, isSelected, selectedIds, clearSelection, isAllSelected, selectAll } =
      useSelection(selectionOptions)

    const TreeRow = useMemo(() => {
      return ({ index, style }: { index: number; style: React.CSSProperties }) => {
        const node = visibleNodes[index]
        const isOpen = openNodes.has(node.oid)
        const nodeIsSelected = isSelected(node.oid)
        const isIndeterminate = calculateIndeterminateState(node, new Set(selectedIds))
        const isHighlighted = node.oid === highlightedNodeId

        return (
          <TreeNode
            node={node}
            isOpen={isOpen}
            isIndeterminate={isIndeterminate}
            isSelected={nodeIsSelected}
            toggleSelection={toggleSelection}
            toggleNode={toggleNode}
            style={style}
            isHighlighted={isHighlighted}
            isShowTags={isShowTags}
            selectionMode={selectionMode}
          />
        )
      }
    }, [
      visibleNodes,
      openNodes,
      isSelected,
      calculateIndeterminateState,
      selectedIds,
      highlightedNodeId,
      toggleSelection,
      toggleNode,
      isShowTags,
      selectionMode,
    ])

    const toggleSelectAll = useCallback(() => {
      if (isAllSelected) {
        clearSelection()
      } else {
        selectAll()
      }
    }, [isAllSelected, selectAll, clearSelection])

    const handleSearchResultClick = useCallback(
      (node: FlatNode) => {
        if (selectSearchResult && !isSelected(node.oid)) toggleSelection(node.oid)

        clearSearch()
        expandToNode(node.oid)
        setHighlightedNodeId(node.oid)

        needsScrollRef.current = true
      },
      [clearSearch, expandToNode, selectSearchResult, isSelected, toggleSelection]
    )

    useEffect(() => {
      if (needsScrollRef.current && highlightedNodeId) {
        const nodeIndex = visibleNodes.findIndex((n) => n.oid === highlightedNodeId)

        if (nodeIndex !== -1 && listRef.current) {
          requestAnimationFrame(() => {
            listRef.current?.scrollToItem(nodeIndex, 'center')

            needsScrollRef.current = false
          })
        }
      }
    }, [visibleNodes, highlightedNodeId, listRef])

    return (
      <Box>
        {showSearch && (
          <Flex w="100%" flexDir="row" alignItems="center" justifyContent="center" p={2} style={{ gap: 8 }}>
            <Box width={searchWidth} minWidth="0">
              <SearchInput
                onChange={handleSearch}
                value={searchTerm}
                onClear={clearSearch}
                placeholder={searchLabel}
                aria-label={searchLabel}
                direction={isRtl ? 'rtl' : 'ltr'}
              />
            </Box>
            {selectionMode === SelectionMode.MULTIPLE && !searchTerm && (
              <Button
                variant="ghost"
                type="button"
                h="36px"
                w="max-content"
                minWidth="100px"
                onClick={searchActions ? selectAll : toggleSelectAll}
                borderRadius="5px"
                fontWeight="normal"
                borderColor={colors.greyLight}
              >
                {isAllSelected && !searchActions ? t_clear : t_select_all}
              </Button>
            )}
            {searchActions}
          </Flex>
        )}

        {searchTerm ? (
          <>
            <Box overflowY="auto" h={listHeight}>
              {searchResult.length > 0 &&
                searchResult.map((node) => (
                  <Box key={node.oid} p={2} cursor="pointer" onClick={() => handleSearchResultClick(node)}>
                    <Flex alignItems="center" justifyContent="space-between">
                      <Box>
                        <Text fontSize="17px" fontWeight={600} color="#202426">
                          {node.name}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {node.path.join(' → ')}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                ))}
              {searchResult.length === 0 && <NoResults />}
            </Box>
          </>
        ) : (
          <Box>
            <List
              ref={listRef}
              direction={isRtl ? 'rtl' : 'ltr'}
              height={listHeight}
              itemCount={visibleNodes.length}
              itemSize={ROW_HEIGHT}
              width="100%"
            >
              {TreeRow}
            </List>
          </Box>
        )}
      </Box>
    )
  }
)

export default OptimizedGroupsTree
